/**
 * The query string as the single source of truth for "what am I looking at".
 *
 * <p>Every lesson picker, tab strip and filter row in the learner app used to remember its selection
 * in `localStorage`. That answered the wrong question: it stored *what did I look at last*, per
 * BROWSER ORIGIN, while the screen needed *what am I looking at*, which belongs to the address. The
 * cost was structural, not cosmetic — a report could not be linked or shared, the back button could
 * not undo a lesson change (the whole selection was invisible to history), two tabs on the same
 * screen overwrote each other through one key, and because the keys are not namespaced by
 * `userProfileId`, switching profile carried the previous learner's lesson over (see
 * `resolveSelectableCoterieType`, which exists to survive exactly that).
 *
 * <p>There is deliberately NO localStorage fallback behind these params. An absent param means the
 * screen's default, full stop — so a bare route always opens the same way, for everyone, on every
 * device. That is also what keeps the read path honest: the value is DERIVED from the URL on every
 * render rather than copied into `useState`, which is what makes back/forward work for free and makes
 * a shared link correct on its FIRST render (no default-then-correct flash, and no data fetch fired
 * for a selection the URL never asked for).
 *
 * <p>These helpers are the pure half; `useUrlSelection` is the hook over them. Validating a param
 * against the options actually on offer is NOT here on purpose: that rule already exists as
 * `resolveSelectableCoterieType`, whose docblock spells out why an off-list value is worse than no
 * value (`GlassSelect` renders `options[0]`, so the picker silently renames itself). A URL param is
 * user-authored — hand-edited, bookmarked from another profile, shared by a learner whose data has
 * since changed — so it needs that rule at least as much as a remembered value did.
 */

export type UrlParamValue = string | number | readonly string[] | null | undefined;

/**
 * Merges `updates` into a query string and returns the next one (`"?a=b"`, or `""` when nothing is
 * left). Absent-means-default is the encoding rule: `null`, `undefined`, `""` and `[]` DELETE their
 * key rather than writing an empty value, so a URL never carries a param that means nothing, and a
 * screen sitting on its defaults has a clean address.
 *
 * <p>Unrelated params are preserved — several selections share one URL, so an update to one must
 * never drop another's.
 *
 * @param search the current query string, with or without its leading "?"
 * @param updates key → value; arrays are written as a comma-separated list
 */
export const applyUrlParamUpdates = (
  search: string,
  updates: Record<string, UrlParamValue>,
): string => {
  const next = new URLSearchParams(search || "");
  for (const key of Object.keys(updates)) {
    const value = updates[key];
    if (value === null || value === undefined || value === "") {
      next.delete(key);
      continue;
    }
    if (Array.isArray(value)) {
      if (value.length === 0) {
        next.delete(key);
      } else {
        next.set(key, value.join(","));
      }
      continue;
    }
    next.set(key, String(value));
  }
  const nextSearch = next.toString();
  return nextSearch ? `?${nextSearch}` : "";
};

/**
 * A multi-select param as a list. An absent param is an empty list — NOT "everything" — because that
 * is what the callers' own `[]` default means.
 */
export const readUrlParamList = (
  search: string | URLSearchParams,
  key: string,
): string[] => {
  const params =
    typeof search === "string" ? new URLSearchParams(search || "") : search;
  const raw = params.get(key);
  if (!raw) return [];
  return raw
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
};

/**
 * A page-index param. Anything a hand-edited URL can carry that is not a non-negative integer —
 * `?page=abc`, `?page=-1`, `?page=1.5`, `?page=1e9` — falls back rather than being adopted: a page
 * index is used to slice an array, so a junk value renders an empty list that looks like "you have
 * nothing" instead of "that page does not exist".
 */
export const readUrlParamNumber = (
  search: string | URLSearchParams,
  key: string,
  fallback: number,
): number => {
  const params =
    typeof search === "string" ? new URLSearchParams(search || "") : search;
  const raw = params.get(key);
  if (raw === null || raw.trim() === "") return fallback;
  if (!/^\d+$/.test(raw.trim())) return fallback;
  const parsed = Number(raw.trim());
  if (!Number.isSafeInteger(parsed)) return fallback;
  return parsed;
};
